/**
 * L-Trust (L°) - LOCARORA Trust Temperature System
 *
 * A trust scoring system that visualizes user reliability as temperature.
 * Initial score: 29.0° (Warm state)
 * Range: 0.0° - 100.0°
 *
 * The customer score column (`customer_profiles.l_trust_score`) is
 * `numeric(4,1)`, i.e. it carries one decimal place (e.g. 30.5).
 * PostgREST can serialize `numeric` as a JSON string ("29.0"), so every
 * value read from the database must pass through `coerceLTrustScore()`.
 */

export type LTrustGrade = "cold" | "cool" | "warm" | "hot" | "blazing";

/** Default score assigned to a customer profile (DB default: 29.0) */
export const L_TRUST_DEFAULT_SCORE = 29;

/** Number of decimal places the score is stored/displayed with */
export const L_TRUST_SCORE_DECIMALS = 1;

/**
 * Normalize a raw L-Trust score coming from the database or props.
 *
 * `numeric` columns may arrive as a JSON string ("29.0"), `null` or
 * `undefined`. This coerces to a finite number and falls back to the
 * default score when the value is unusable.
 */
export function coerceLTrustScore(
  value: unknown,
  fallback: number = L_TRUST_DEFAULT_SCORE
): number {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Clamp a score to the valid 0 - 100 range (after coercion).
 */
export function clampLTrustScore(score: unknown): number {
  return Math.max(0, Math.min(100, coerceLTrustScore(score)));
}

export interface LTrustInfo {
  score: number;
  grade: LTrustGrade;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  gradientBg: string;
  percentage: number;
}

/**
 * Get L-Trust grade based on score
 *
 * Grade ranges (matching the integer boundaries of the `l_trust_grades`
 * table, e.g. cold has temp_end = 14):
 * - Cold: 0° ~ 14° (신뢰도 매우 낮음)
 * - Cool: 15° ~ 24° (신뢰도 낮음)
 * - Warm: 25° ~ 44° (기본 상태, 29°가 초기값)
 * - Hot: 45° ~ 64° (신뢰도 높음)
 * - Blazing: 65° ~ 100° (VIP 사용자)
 *
 * Fractional scores are floored so the DB grade table stays authoritative:
 * 14.5 → cold, 24.9 → cool, 45.0 → hot.
 */
export function getLTrustGrade(score: unknown): LTrustGrade {
  const whole = Math.floor(clampLTrustScore(score));
  if (whole < 15) return "cold";
  if (whole < 25) return "cool";
  if (whole < 45) return "warm";
  if (whole < 65) return "hot";
  return "blazing";
}

/**
 * Get L-Trust label for grade
 */
export function getLTrustLabel(grade: LTrustGrade): string {
  const labels: Record<LTrustGrade, string> = {
    cold: "Cold",
    cool: "Cool",
    warm: "Warm",
    hot: "Hot",
    blazing: "Blazing",
  };
  return labels[grade];
}

/**
 * Get gradient background for card
 */
export function getLTrustCardGradient(grade: LTrustGrade): string {
  const gradients: Record<LTrustGrade, string> = {
    cold: "bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50",
    cool: "bg-gradient-to-br from-cyan-50 via-cyan-100 to-teal-50",
    warm: "bg-gradient-to-br from-orange-50 via-amber-100 to-yellow-50",
    hot: "bg-gradient-to-br from-red-50 via-orange-100 to-amber-50",
    blazing: "bg-gradient-to-br from-purple-50 via-pink-100 to-rose-50",
  };
  return gradients[grade];
}

/**
 * Get Tailwind color classes for grade
 */
export function getLTrustColors(grade: LTrustGrade): {
  color: string;
  bgColor: string;
  borderColor: string;
} {
  const colors: Record<
    LTrustGrade,
    { color: string; bgColor: string; borderColor: string }
  > = {
    cold: {
      color: "text-blue-500",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-200",
    },
    cool: {
      color: "text-cyan-500",
      bgColor: "bg-cyan-100",
      borderColor: "border-cyan-200",
    },
    warm: {
      color: "text-orange-500",
      bgColor: "bg-orange-100",
      borderColor: "border-orange-200",
    },
    hot: {
      color: "text-red-500",
      bgColor: "bg-red-100",
      borderColor: "border-red-200",
    },
    blazing: {
      color: "text-purple-500",
      bgColor: "bg-purple-100",
      borderColor: "border-purple-200",
    },
  };
  return colors[grade];
}

/**
 * Get gradient color for progress bar
 */
export function getLTrustGradient(grade: LTrustGrade): string {
  const gradients: Record<LTrustGrade, string> = {
    cold: "from-blue-400 to-blue-500",
    cool: "from-cyan-400 to-cyan-500",
    warm: "from-orange-400 to-orange-500",
    hot: "from-red-400 to-red-500",
    blazing: "from-purple-400 to-purple-500",
  };
  return gradients[grade];
}

/**
 * Get complete L-Trust info for a score
 */
export function getLTrustInfo(score: unknown): LTrustInfo {
  const clampedScore = clampLTrustScore(score);
  const grade = getLTrustGrade(clampedScore);
  const colors = getLTrustColors(grade);

  return {
    score: clampedScore,
    grade,
    label: getLTrustLabel(grade),
    ...colors,
    gradientBg: getLTrustCardGradient(grade),
    percentage: clampedScore,
  };
}

/**
 * Format the numeric part of a score with the stored precision.
 * e.g. 30.5 → "30.5", "29.0" → "29.0", 29 → "29.0"
 */
export function formatLTrustScoreValue(score: unknown): string {
  return clampLTrustScore(score).toFixed(L_TRUST_SCORE_DECIMALS);
}

/**
 * Format score for display, keeping the existing "L°" unit convention.
 * e.g. 30.5 → "L°30.5"
 */
export function formatLTrustScore(score: unknown): string {
  return `L°${formatLTrustScoreValue(score)}`;
}

/**
 * L-Trust change reasons (for history display)
 */
export const L_TRUST_REASONS = {
  // Positive
  first_reservation: { change: 3, label: "첫 예약 완료" },
  review_written: { change: 2, label: "리뷰 작성" },
  reservation_completed: { change: 1, label: "예약 완료" },
  id_verified: { change: 5, label: "신분증 인증" },
  license_verified: { change: 3, label: "운전면허 인증" },
  referral_signup: { change: 2, label: "추천인 가입" },
  monthly_bonus: { change: 1, label: "월간 활동 보너스" },

  // Negative
  no_show: { change: -10, label: "노쇼" },
  late_cancellation: { change: -3, label: "24시간 이내 취소" },
  vehicle_damage: { change: -15, label: "차량 손상" },
  report_minor: { change: -5, label: "신고 접수 (경미)" },
  report_major: { change: -20, label: "신고 접수 (심각)" },
  inactivity: { change: -1, label: "30일 미활동" },
} as const;

export type LTrustReasonKey = keyof typeof L_TRUST_REASONS;
